import {useQuery} from '@tanstack/react-query'
import {sanityClient} from '@/lib/sanity'
import {languagePageQuery} from '@/lib/sanityQueries'
import type {LanguagePage, LanguagePageVariant} from '@/types/languagePage'

const fetchPage = async (
  variant: LanguagePageVariant,
  slug: string,
  locale?: string,
): Promise<LanguagePage | null> => {
  const localized = Boolean(locale && locale !== 'en')
  const query = languagePageQuery(variant, locale)
  const params = localized ? {slug, locale} : {slug}
  const result = await sanityClient.fetch<Omit<LanguagePage, 'variant'> | null>(query, params)
  if (result) return {...result, variant} as LanguagePage
  if (!localized) return null

  // No translation imported for this locale yet: show the English page, as
  // LanguagePagesController does for the same URL (and seeds into SSR, so
  // without this the first refetch turned it into a 404). English at a /fr/
  // URL must stay out of the index, like the server's noindex.
  const english = await sanityClient.fetch<Omit<LanguagePage, 'variant'> | null>(
    languagePageQuery(variant),
    {slug},
  )
  if (!english) return null
  return {...english, variant, noIndex: true} as LanguagePage
}

// locale is threaded into the queryKey so it matches the SSR seed (see
// server.tsx seedQueryClient) and so each locale caches independently.
export const useLanguagePage = (
  variant: LanguagePageVariant,
  slug: string | undefined,
  locale?: string,
) =>
  useQuery({
    queryKey: ['languagePage', variant, slug, locale ?? 'en'],
    queryFn: () => {
      if (!slug) return Promise.resolve<LanguagePage | null>(null)
      return fetchPage(variant, slug.toLowerCase(), locale)
    },
    enabled: Boolean(slug),
    staleTime: 1000 * 60,
  })
